import React, { useEffect, useState } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../AuthContext";
import { api } from "../api/client";
import "./ModernLayout.css";
import "./surfaces.css";
import { menuText } from "../i18n/messages";

const SCOPED_PATHS = new Set([
  "/tum-is-emirleri",
  "/talep-is-emirleri",
  "/taslak-is-emirleri",
  "/onayli-is-emirleri",
  "/devam-eden-is-emirleri",
  "/pasif-devam-is-emirleri",
  "/tamamlanan-is-emirleri",
  "/iptal-is-emirleri",
  "/musteri-listele",
  "/talep-ekle",
]);

const MENU = [
  {
    title: "Teklifler", icon: "fas fa-file-invoice", section: "teklif",
    items: [
      { title: "Pano", to: "/" },
      { title: "Teklif Ekle", to: "/teklif-olustur" },
      { title: "Tüm Teklifler", to: "/tum-teklifler" },
      { title: "Hazırlanan Teklifler", to: "/hazirlananlar" },
      { title: "Onay Bekleyen Teklifler", to: "/onay-bekleyenler" },
      { title: "Onaylanan Teklifler", to: "/onaylananlar" },
      { title: "Tamamlanan Teklifler", to: "/tamamlananlar" },
      { title: "Sevk Edilenler", to: "/sevk-edilenler" },
      { title: "Reddedilen Teklifler", to: "/iptal-edilenler" },
    ],
  },
  {
    title: "Raporlar", icon: "fas fa-chart-bar", section: "reports",
    items: [
      { title: "Satış Raporu", to: "/satis-grafikleri" },
      { title: "Detaylı Satış Raporu", to: "/detayli-teklif-satis-raporu" },
      { title: "Ödeme Takip", to: "/odeme-takip-raporu" },
    ],
  },
  {
    title: "Sözleşmeler", icon: "fas fa-file-contract", section: "sozlesmeler",
    items: [{ title: "Onaylananlar", to: "/onayli-sozlesmeler" }],
  },
  {
    title: "İş Emri Takvimi", icon: "fas fa-calendar-alt", section: "is_emri_takvimi",
    items: [{ title: "İş Emri Takvimi", to: "/task-table" }],
  },
  {
    title: "İş Emirleri", icon: "fas fa-tasks", section: "isemri",
    items: [
      { title: "İş Emri Ekle", to: "/talep-ekle" },
      { title: "Takvim", to: "/task-table" },
      { title: "Tüm İş Emirleri", to: "/tum-is-emirleri" },
      { title: "Talep İş Emirleri", to: "/talep-is-emirleri" },
      { title: "Taslak İş Emirleri", to: "/taslak-is-emirleri" },
      { title: "Final İş Emirleri", to: "/onayli-is-emirleri", badgeDurum: "Final" },
      { title: "Devam Eden İş Emirleri", to: "/devam-eden-is-emirleri", badgeDurum: "Devam" },
      { title: "Pasif Devam İş Emirleri", to: "/pasif-devam-is-emirleri", badgeDurum: "Pasif Devam" },
      { title: "Tamamlanan İş Emirleri", to: "/tamamlanan-is-emirleri", badgeDurum: "Tamamlandi" },
      { title: "İptal İş Emirleri", to: "/iptal-is-emirleri" },
      { title: "İzinler", to: "/izin-listesi" },
      { title: "Vizeler", to: "/vize-listesi" },
    ],
  },
  {
    title: "Müşteriler", icon: "fas fa-users", section: "musteriler",
    items: [{ title: "Müşteri Listesi", to: "/musteri-listele" }],
  },
  {
    title: "Makineler", icon: "fas fa-industry", section: "makineler",
    items: [
      { title: "Makine Listesi", to: "/makine-listele" },
      { title: "Lokasyon Güncelle", to: "/makine-lokasyon-guncelle" },
    ],
  },
  {
    title: "Yönetim", icon: "fas fa-user-shield", section: "kullanicilar",
    items: [{ title: "Kullanıcı Yönetimi", to: "/kullanici-yonetimi" }],
  },
];

function pagePath(to) {
  if (!to || to === "/") return "/";
  return to.startsWith("/") ? to : `/${to}`;
}

function eskiPath(pathname) {
  if (!pathname || pathname === "/") return "/eski";
  return `/eski${pathname.startsWith("/") ? pathname : `/${pathname}`}`;
}

function isYonetici(user) {
  return user?.role === "admin" || user?.role === "superadmin";
}

function pathMatches(pathname, to) {
  const p = pathname.replace(/\/$/, "") || "/";
  const t = to.replace(/\/$/, "") || "/";
  if (t === "/") return p === "/";
  return p === t || p.startsWith(`${t}/`);
}

export default function ModernLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const userName = user?.full_name || user?.username || "";
  const [sections, setSections] = useState(null);
  const [unreadBadges, setUnreadBadges] = useState({});
  const [open, setOpen] = useState(false);
  const [mini, setMini] = useState(false);

  const here = location.pathname;
  const activeGroupTitle = (() => {
    let best = null;
    let bestLen = -1;
    for (const group of MENU) {
      for (const item of group.items) {
        const target = pagePath(item.to);
        if (pathMatches(here, target) && target.length > bestLen) {
          best = group.title;
          bestLen = target.length;
        }
      }
    }
    return best;
  })();
  const [openGroup, setOpenGroup] = useState(activeGroupTitle);
  const activeItem = MENU.flatMap((g) => g.items).find((item) => pathMatches(here, pagePath(item.to)));

  useEffect(() => {
    document.body.classList.add("ct-yeni-on");
    return () => document.body.classList.remove("ct-yeni-on");
  }, []);

  useEffect(() => {
    let cancelled = false;
    api.get("/users/me/menu-sections").then((res) => {
      if (cancelled) return;
      const list = Array.isArray(res.data) ? res.data : [];
      setSections(list);
      try {
        const calViewer = list.includes("is_emri_takvimi") && !list.includes("isemri");
        sessionStorage.setItem("ct_cal_ro", calViewer ? "1" : "0");
        sessionStorage.setItem("ct_cal_only", calViewer ? "1" : "0");
      } catch (e) { /* ignore */ }
    }).catch(() => {
      // Fail-closed: yetki gelmezse menüyü açma
      if (!cancelled) setSections([]);
    });
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id) {
      setUnreadBadges({});
      return undefined;
    }
    let cancelled = false;
    const load = () => {
      api.get("/is-emirleri/unread-badges").then((res) => {
        if (!cancelled && res.data && typeof res.data === "object") setUnreadBadges(res.data);
      }).catch(() => {
        if (!cancelled) setUnreadBadges({});
      });
    };
    load();
    const timer = setInterval(load, 60000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [user?.id, here]);

  useEffect(() => {
    if (activeGroupTitle) setOpenGroup(activeGroupTitle);
    setOpen(false);
  }, [here, activeGroupTitle]);

  const calendarViewer = Array.isArray(sections)
    && sections.includes("is_emri_takvimi")
    && !sections.includes("isemri");
  useEffect(() => {
    if (!calendarViewer) return;
    const bare = (here.replace(/^\/eski/, "") || "/").replace(/\/$/, "") || "/";
    if (bare === "/task-table" || bare === "/tum-is-emirleri") return;
    navigate("/task-table", { replace: true });
  }, [calendarViewer, here, navigate]);

  const scoped = user?.tenant_id != null && user?.tenant_id !== "";
  const visibleMenu = MENU.filter((group) => {
    if (sections == null) return false; // yetki yüklenene kadar boş
    if (group.section === "is_emri_takvimi" && !(sections.includes("is_emri_takvimi") && !sections.includes("isemri"))) return false;
    if (scoped) return group.section === "isemri" || group.section === "musteriler";
    if (user?.role === "admin" || user?.role === "superadmin") return true;
    return sections.includes(group.section);
  }).map((group) => (
    scoped
      ? { ...group, items: group.items.filter((item) => SCOPED_PATHS.has(pagePath(item.to))) }
      : group
  ));
  if (calendarViewer && !visibleMenu.some((group) => group.section === "isemri")) {
    visibleMenu.push({
      title: "İş Emirleri",
      icon: "fas fa-tasks",
      section: "isemri",
      items: [{ title: "Tüm İş Emirleri", to: "/tum-is-emirleri" }],
    });
  }

  return (
    <div className={`ct-yeni${open ? " is-open" : ""}${mini ? " is-mini" : ""}`}>
      <button type="button" className="ct-yeni__backdrop" aria-label={menuText("Menüyü kapat")} onClick={() => setOpen(false)} />
      <aside className="ct-yeni__side">
        <button
          type="button"
          className="ct-yeni__brand"
          aria-label={mini ? "Menüyü genişlet" : "Menüyü küçült"}
          onClick={() => setMini((value) => !value)}
        >
          <img className="ct-yeni__logo-full" src="/assets/img/menu_logo.png" alt="Coiltech" />
          <img className="ct-yeni__logo-mini" src="/assets/img/menu_kck_logo.png" alt="" />
        </button>
        <nav className="ct-yeni__nav">
          {visibleMenu.map((group) => {
            const isOpen = calendarViewer || openGroup === group.title;
            const groupBadge = group.items.reduce((sum, item) => {
              const n = item.badgeDurum && unreadBadges[item.badgeDurum]
                ? Number(unreadBadges[item.badgeDurum])
                : 0;
              return sum + (Number.isFinite(n) ? n : 0);
            }, 0);
            return (
              <div key={group.title} className={`ct-yeni__group${isOpen ? " is-open" : ""}`}>
                <button
                  type="button"
                  className="ct-yeni__group-btn"
                  onClick={() => {
                    if (calendarViewer) return;
                    setOpenGroup(isOpen ? null : group.title);
                  }}
                >
                  <i className={group.icon} aria-hidden="true" />
                  <span>{menuText(group.title)}</span>
                  {groupBadge > 0 && <em>{groupBadge}</em>}
                </button>
                {isOpen && (
                  <div className="ct-yeni__items">
                    {group.items.map((item) => {
                      const target = pagePath(item.to);
                      const badgeN = item.badgeDurum && unreadBadges[item.badgeDurum]
                        ? Number(unreadBadges[item.badgeDurum])
                        : 0;
                      const active = pathMatches(here, target);
                      return (
                        <Link key={item.to} to={target} className={active ? "is-active" : ""}>
                          <span>{menuText(item.title)}</span>
                          {badgeN > 0 && <em>{badgeN}</em>}
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </nav>
      </aside>
      <div className="ct-yeni__main">
        <header className="ct-yeni__top">
          <button type="button" className="ct-yeni__menu" aria-label={menuText("Menü")} onClick={() => setOpen((v) => !v)}>
            <i className="fas fa-bars" aria-hidden="true" />
          </button>
          <strong>{menuText(activeItem?.title || "Coiltech Servis")}</strong>
          <div className="ct-yeni__top-end">
            {isYonetici(user) ? <Link to={eskiPath(here)}>Eski arayüz</Link> : null}
            <span>{userName || "Merhaba"}</span>
            <button
              type="button"
              onClick={async () => {
                await logout();
                navigate("/login", { replace: true });
              }}
            >
              {menuText("Çıkış")}
            </button>
          </div>
        </header>
        <div className="ct-yeni__content">
          <Outlet />
        </div>
      </div>
    </div>
  );
}
